// scroll-end.directive.ts
import {
  AfterViewInit,
  DestroyRef,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  inject,
} from '@angular/core';
import { fromEvent } from 'rxjs';
import { auditTime, map } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Directive({
  selector: '[appScrollEnd]',
  standalone: true,
})
export class ScrollEndDirective implements AfterViewInit {
  private el = inject(ElementRef<HTMLElement>);
  private destroyRef = inject(DestroyRef);
  private zone = inject(NgZone);

  /** Preload distance from bottom before firing (in px) */
  @Input() thresholdPx = 200;

  /** How far user must scroll up (away from bottom zone) to re-arm another emit (in px) */
  @Input() rearmDeltaPx = 300;

  /** Debounce/audit time for scroll events (in ms) */
  @Input() auditMs = 100;

  /** Enable/disable the directive at runtime */
  @Input() enabled = true;

  /** Emitted once when near bottom (re-arms after scrolling up by `rearmDeltaPx`) */
  @Output() reachedEnd = new EventEmitter<void>();

  /** (Optional) Emits remaining pixels to bottom for diagnostics/UI */
  @Output() remainingPx = new EventEmitter<number>();

  private armed = true;

  ngAfterViewInit(): void {
    // Handle scroll outside Angular to reduce CD thrashing; re-enter only when emitting.
    fromEvent(this.el.nativeElement, 'scroll')
      .pipe(
        auditTime(this.auditMs),
        map(() => this.remaining()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((remaining) => {
        this.remainingPx.emit(remaining);

        if (!this.enabled) return;

        // Re-arm when user has scrolled up enough away from the bottom zone
        if (remaining > this.thresholdPx + this.rearmDeltaPx) {
          this.armed = true;
        }

        // Fire once per approach to bottom
        if (this.armed && remaining <= this.thresholdPx) {
          this.armed = false;
          this.zone.run(() => this.reachedEnd.emit());
        }
      });
  }


  /** Remaining pixels until bottom */
  private remaining(): number {
    const el = this.el.nativeElement;
    return el.scrollHeight - el.scrollTop - el.clientHeight;
  }

  /** You can call this from the host component after a load to allow the next trigger */
  public rearm(): void {
    this.armed = true;
  }
}
